"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.legacyMetricMigrationInventory = exports.legacyMetricMigrationCrosswalk = void 0;
const master_data_1 = require("../master-data");
const effect_1 = require("../master-data/effect");
const metric_catalog_1 = require("./metric-catalog");
const mapped = (v1Metric, configurationTranslation = { filters: 'manual', weights: 'manual' }) => ({ disposition: 'mapped', v1Metric, configurationTranslation });
const semanticChange = (v1Metric, change, configurationTranslation = { filters: 'manual', weights: 'manual' }) => ({
    disposition: 'semantic_change',
    v1Metric,
    semanticChange: change,
    configurationTranslation,
});
const sourceSelectionChanged = (v1Metric) => semanticChange(v1Metric, 'source_selection_changed');
const reportedPeriodOrCurrencyChanged = (v1Metric) => semanticChange(v1Metric, 'reported_period_or_currency_changed');
const calculationInputsChanged = (v1Metric) => semanticChange(v1Metric, 'calculation_inputs_changed');
const manualConfigurationTranslation = {
    filters: 'manual',
    weights: 'manual',
};
const automaticFilterTranslation = {
    filters: 'automatic',
    weights: 'manual',
};
const conditionalFilterTranslation = {
    filters: 'conditional',
    weights: 'manual',
};
const conditionalWeightTranslation = {
    filters: 'manual',
    weights: 'conditional',
};
/**
 * Explicit legacy-to-V1 decisions. Absence is itself meaningful: that field is
 * classified as planned until a source-backed metric and its semantics exist.
 */
const implementedLegacyMetricMappings = {
    companyBuzzCount: sourceSelectionChanged('signal.buzz.article_count.within_14d_mapped'),
    orgnr: sourceSelectionChanged('company.identity.orgnr'),
    companyName: sourceSelectionChanged('company.identity.name'),
    previousNames: sourceSelectionChanged('company.identity.previous_names'),
    address: sourceSelectionChanged('company.location.address'),
    website: sourceSelectionChanged('company.contact.website'),
    phone: sourceSelectionChanged('company.contact.phone'),
    email: sourceSelectionChanged('company.contact.email'),
    descriptionEn: sourceSelectionChanged('company.profile.description_en'),
    financialsUpdatedDate: sourceSelectionChanged('financial.reporting.updated_date.current'),
    fiscalYear: reportedPeriodOrCurrencyChanged('financial.reporting.fiscal_year.current'),
    annualReportNumberOfEmployees: reportedPeriodOrCurrencyChanged('financial.reporting.employees.current'),
    fiscalYearLength: reportedPeriodOrCurrencyChanged('financial.reporting.fiscal_year_length.current'),
    lfyMonth: reportedPeriodOrCurrencyChanged('financial.reporting.fiscal_year_end_month.current'),
    currency: semanticChange('financial.reporting.currency.current', 'reported_period_or_currency_changed', conditionalFilterTranslation),
    sniClassificationCode: mapped('company.industry.sni_code', automaticFilterTranslation),
    sniClassification: semanticChange('company.industry.sni_classification', 'source_selection_changed', conditionalFilterTranslation),
    operationalStatus: mapped('company.operational_status', automaticFilterTranslation),
    legalEntityType: mapped('company.legal_entity_type'),
    companyCategory: mapped('company.category'),
    companyStructure: mapped('company.structure'),
    legalForm: mapped('company.legal_form'),
    listingType: mapped('company.listing_type'),
    registrationDate: mapped('company.registration_date', automaticFilterTranslation),
    hasPrivateEquityOwner: mapped('company.ownership.has_private_equity_owner', automaticFilterTranslation),
    privateEquityOwner: mapped('company.ownership.private_equity_owner', conditionalFilterTranslation),
    hasForeignUltimateParent: mapped('company.ownership.has_foreign_ultimate_parent', automaticFilterTranslation),
    ultimateParentCompany: mapped('company.ownership.ultimate_parent_company_name', conditionalFilterTranslation),
    ultimateParentOrgNo: mapped('company.ownership.ultimate_parent_orgnr', conditionalFilterTranslation),
    parentCompanyName: sourceSelectionChanged('company.ownership.parent_company_name'),
    numberOfOwners: mapped('company.ownership.owner_count', automaticFilterTranslation),
    ceo: mapped('company.leadership.ceo_name', conditionalFilterTranslation),
    ceoBirthYear: mapped('company.leadership.ceo_birth_year', conditionalFilterTranslation),
    chairman: mapped('company.leadership.chairman_name', conditionalFilterTranslation),
    chairmanBirthYear: mapped('company.leadership.chairman_birth_year', conditionalFilterTranslation),
    signatory: mapped('company.leadership.signatory', conditionalFilterTranslation),
    hasShareIssues: mapped('company.capital.has_share_issues', automaticFilterTranslation),
    numberOfShares: mapped('company.capital.share_count', automaticFilterTranslation),
    zipcode: mapped('company.location.postal_code', automaticFilterTranslation),
    muncipality: mapped('company.location.municipality', automaticFilterTranslation),
    county: mapped('company.location.county', automaticFilterTranslation),
    region: mapped('company.location.region', automaticFilterTranslation),
    iso3166: semanticChange('company.country.iso3166', 'value_derivation_changed', conditionalFilterTranslation),
    city: semanticChange('company.location.city', 'source_selection_changed', conditionalFilterTranslation),
    domain: sourceSelectionChanged('web.domain.current'),
    snapchatHandle: sourceSelectionChanged('web.social.snapchat_handles'),
    h1Content: sourceSelectionChanged('web.page.h1_content'),
    h1ContentWords: sourceSelectionChanged('web.page.h1_content_word_counts'),
    h1ContentChars: sourceSelectionChanged('web.page.h1_content_character_counts'),
    finalUrl: sourceSelectionChanged('web.page.final_url'),
    pageLoadTimeTti: sourceSelectionChanged('web.performance.page_load_time_tti_ms'),
    webVitalsTtfb: sourceSelectionChanged('web.performance.time_to_first_byte_ms'),
    webVitalsFcp: sourceSelectionChanged('web.performance.first_contentful_paint_ms'),
    webVitalsLcp: sourceSelectionChanged('web.performance.largest_contentful_paint_ms'),
    webVitalsCls: sourceSelectionChanged('web.performance.cumulative_layout_shift'),
    webVitalsTti: mapped('web.performance.dom_interactive_ms'),
    webVitalsInp: sourceSelectionChanged('web.performance.interaction_to_next_paint_ms'),
    sizeDocument: sourceSelectionChanged('web.performance.document_size_bytes'),
    sizeScript: sourceSelectionChanged('web.performance.script_size_bytes'),
    sizeStylesheet: sourceSelectionChanged('web.performance.stylesheet_size_bytes'),
    sizeImage: sourceSelectionChanged('web.performance.image_size_bytes'),
    sizeMedia: sourceSelectionChanged('web.performance.media_size_bytes'),
    sizeFont: sourceSelectionChanged('web.performance.font_size_bytes'),
    sizeXhr: sourceSelectionChanged('web.performance.xhr_size_bytes'),
    sizePing: sourceSelectionChanged('web.performance.ping_size_bytes'),
    sizeOther: sourceSelectionChanged('web.performance.other_resource_size_bytes'),
    cookieConsent: sourceSelectionChanged('web.page.cookie_consent_detected'),
    platform: sourceSelectionChanged('web.platform.current'),
    isEcommerce: sourceSelectionChanged('web.ecommerce.is_ecommerce'),
    toolGoogletagmanager: sourceSelectionChanged('web.analytics.google_tag_manager_detected'),
    toolHubspot: sourceSelectionChanged('web.marketing.hubspot_detected'),
    toolSalesforce: sourceSelectionChanged('web.marketing.salesforce_detected'),
    toolKlaviyo: sourceSelectionChanged('web.marketing.klaviyo_detected'),
    toolHotjar: sourceSelectionChanged('web.experience.hotjar_detected'),
    toolOptimizely: sourceSelectionChanged('web.experimentation.optimizely_detected'),
    toolWordpress: sourceSelectionChanged('web.technology.wordpress_detected'),
    toolShopify: sourceSelectionChanged('web.technology.shopify_detected'),
    ...Object.fromEntries(metric_catalog_1.realtimeDomainAnalyticsMetrics
        .filter(({ legacyMetricKey }) => legacyMetricKey !== 'gconvpAccount')
        .map(({ legacyMetricKey, key }) => [legacyMetricKey, sourceSelectionChanged(key)])),
    ...Object.fromEntries(metric_catalog_1.realtimeDomainMetadataMetrics.map(({ legacyMetricKey, key }) => [legacyMetricKey, sourceSelectionChanged(key)])),
    sitemapCount: semanticChange('web.page.sitemap_count', 'value_derivation_changed', conditionalFilterTranslation),
    sitemaps: semanticChange('web.page.sitemap_url', 'value_derivation_changed', conditionalFilterTranslation),
    sitemapUrlCount: semanticChange('web.page.sitemap_url_count', 'value_derivation_changed', conditionalFilterTranslation),
    accessViolationScore: sourceSelectionChanged('web.accessibility.violation_score'),
    violations: sourceSelectionChanged('web.accessibility.violations'),
    imgAltRatio: sourceSelectionChanged('web.accessibility.image_alt_ratio'),
    imgCount: sourceSelectionChanged('web.accessibility.image_count'),
    imgAltCount: sourceSelectionChanged('web.accessibility.image_alt_count'),
    imgMissingAltCount: sourceSelectionChanged('web.accessibility.image_missing_alt_count'),
    imgTitleCount: sourceSelectionChanged('web.accessibility.image_title_count'),
    trackersBeforeConsentCount: sourceSelectionChanged('web.privacy.trackers_before_consent_count'),
    numberOfEmployees: semanticChange('workforce.employees.current', 'source_selection_changed', conditionalFilterTranslation),
    annualReportEmployeeGrowth: calculationInputsChanged('workforce.employees.growth_1y'),
    identifiedOrgnrCount: mapped('company.group.identified_orgnr_count'),
    identifiedOrgnrDelta6m: mapped('company.group.identified_orgnr_delta_6m'),
    identifiedOrgnrDelta1y: mapped('company.group.identified_orgnr_delta_1y'),
    identifiedOrgnrDelta3y: mapped('company.group.identified_orgnr_delta_3y'),
    identifiedOrgnrDelta5y: mapped('company.group.identified_orgnr_delta_5y'),
    numberOfAddresses: sourceSelectionChanged('company.workplaces.active_count'),
    activeJobposts: sourceSelectionChanged('hiring.active_jobposts.current'),
    activeJobpostsPerEmployee: calculationInputsChanged('hiring.active_jobposts.per_employee'),
    workspaceSizeInM2: semanticChange('crm.workspace.current_m2', 'tenant_scope_changed'),
    ...Object.fromEntries(metric_catalog_1.realtimeBuzzEventMetrics.map(({ legacyMetricKey, key }) => [
        legacyMetricKey,
        semanticChange(key, 'event_window_changed', conditionalWeightTranslation),
    ])),
    ...Object.fromEntries(metric_catalog_1.realtimePrvTrademarkMetrics.map(({ legacyMetricKey, key }) => [legacyMetricKey, sourceSelectionChanged(key)])),
    ...Object.fromEntries(metric_catalog_1.realtimeApolloOrganizationMetrics.map(({ legacyMetricKey, key }) => [legacyMetricKey, sourceSelectionChanged(key)])),
    ...Object.fromEntries(metric_catalog_1.realtimeApolloHeadcountCurrentMetrics.map(({ legacyMetricKey, key }) => [
        legacyMetricKey,
        sourceSelectionChanged(key),
    ])),
    ...Object.fromEntries(metric_catalog_1.realtimeApolloHeadcountHistoricalMetrics.map(({ legacyMetricKey, key }) => [
        legacyMetricKey,
        sourceSelectionChanged(key),
    ])),
    ...Object.fromEntries(metric_catalog_1.realtimeApolloHeadcountGrowthMetrics.map(({ legacyMetricKey, key }) => [
        legacyMetricKey,
        sourceSelectionChanged(key),
    ])),
    ...Object.fromEntries(metric_catalog_1.realtimeLinkedInCurrentMetrics.flatMap(({ legacyMetricKey, key }) => legacyMetricKey === undefined ? [] : [[legacyMetricKey, sourceSelectionChanged(key)]])),
    ...Object.fromEntries(metric_catalog_1.realtimeLinkedInEmployeeGrowthMetrics.map(({ legacyMetricKey, key }) => [
        legacyMetricKey,
        semanticChange(key, 'value_derivation_changed', conditionalFilterTranslation),
    ])),
    ...Object.fromEntries(metric_catalog_1.realtimeDomainToolMetrics.map(({ legacyMetricKey, key }) => [legacyMetricKey, sourceSelectionChanged(key)])),
    netSales: semanticChange('financial.revenue.current', 'reported_period_or_currency_changed', conditionalFilterTranslation),
    costOfSales: reportedPeriodOrCurrencyChanged('financial.cost_of_sales.current'),
    grossProfit: reportedPeriodOrCurrencyChanged('financial.gross_profit.current'),
    administrativeExpenses: reportedPeriodOrCurrencyChanged('financial.administrative_expenses.current'),
    sellingExpenses: reportedPeriodOrCurrencyChanged('financial.selling_expenses.current'),
    otherExternalExpenses: reportedPeriodOrCurrencyChanged('financial.other_external_expenses.current'),
    personelExpenses: reportedPeriodOrCurrencyChanged('financial.personnel_expenses.current'),
    ebit: reportedPeriodOrCurrencyChanged('financial.ebit.current'),
    ebita: reportedPeriodOrCurrencyChanged('financial.ebita.current'),
    ebitd: reportedPeriodOrCurrencyChanged('financial.ebitd.current'),
    ebitda: reportedPeriodOrCurrencyChanged('financial.ebitda.current'),
    deprAmo: reportedPeriodOrCurrencyChanged('financial.depreciation_and_amortisation.current'),
    depreciation: reportedPeriodOrCurrencyChanged('financial.depreciation.current'),
    amortisation: reportedPeriodOrCurrencyChanged('financial.amortisation.current'),
    netFinancialIncomeOrExpenses: reportedPeriodOrCurrencyChanged('financial.net_financial_income_or_expenses.current'),
    financialIncome: reportedPeriodOrCurrencyChanged('financial.financial_income.current'),
    financialExpenses: reportedPeriodOrCurrencyChanged('financial.financial_expenses.current'),
    preTaxProfit: reportedPeriodOrCurrencyChanged('financial.pre_tax_profit.current'),
    netProfit: reportedPeriodOrCurrencyChanged('financial.net_profit.current'),
    cashEquivalents: reportedPeriodOrCurrencyChanged('financial.cash_equivalents.current'),
    accRec: reportedPeriodOrCurrencyChanged('financial.accounts_receivable.current'),
    inventories: reportedPeriodOrCurrencyChanged('financial.inventories.current'),
    othCurrOpAss: reportedPeriodOrCurrencyChanged('financial.other_current_operating_assets.current'),
    currFinAss: reportedPeriodOrCurrencyChanged('financial.current_financial_assets.current'),
    totalCurrentAssets: reportedPeriodOrCurrencyChanged('financial.total_current_assets.current'),
    ppe: reportedPeriodOrCurrencyChanged('financial.property_plant_and_equipment.current'),
    goodwillIntangAss: reportedPeriodOrCurrencyChanged('financial.goodwill_and_intangible_assets.current'),
    nCurrFinRec: reportedPeriodOrCurrencyChanged('financial.non_current_financial_receivables.current'),
    othNCurrOpAss: reportedPeriodOrCurrencyChanged('financial.other_non_current_operating_assets.current'),
    totalNonCurrentAssets: reportedPeriodOrCurrencyChanged('financial.total_non_current_assets.current'),
    totalAssets: reportedPeriodOrCurrencyChanged('financial.total_assets.current'),
    accPay: reportedPeriodOrCurrencyChanged('financial.accounts_payable.current'),
    currFinLiab: reportedPeriodOrCurrencyChanged('financial.current_financial_liabilities.current'),
    postEmplLiab: reportedPeriodOrCurrencyChanged('financial.post_employee_liabilities.current'),
    othCurrOpLiab: reportedPeriodOrCurrencyChanged('financial.other_current_operating_liabilities.current'),
    totalCurrentLiabilities: reportedPeriodOrCurrencyChanged('financial.total_current_liabilities.current'),
    othNCurrOpLiab: reportedPeriodOrCurrencyChanged('financial.other_non_current_operating_liabilities.current'),
    nCurrFinLiab: reportedPeriodOrCurrencyChanged('financial.non_current_financial_liabilities.current'),
    totalNonCurrLiab: reportedPeriodOrCurrencyChanged('financial.total_non_current_liabilities.current'),
    shareholdEq: reportedPeriodOrCurrencyChanged('financial.shareholder_equity.current'),
    retainedEarnings: reportedPeriodOrCurrencyChanged('financial.retained_earnings.current'),
    minInt: reportedPeriodOrCurrencyChanged('financial.minority_interest.current'),
    totalEquity: reportedPeriodOrCurrencyChanged('financial.total_equity.current'),
    dividends: reportedPeriodOrCurrencyChanged('financial.dividends.current'),
    cashFlowBeforeFinAct: reportedPeriodOrCurrencyChanged('financial.cash_flow_before_financial_activities.current'),
    cashFlowOperating: reportedPeriodOrCurrencyChanged('financial.cash_flow_operating.current'),
    netCapex: reportedPeriodOrCurrencyChanged('financial.net_capex.current'),
    netDebt: reportedPeriodOrCurrencyChanged('financial.net_debt.current'),
    marketCap: reportedPeriodOrCurrencyChanged('financial.market_cap.current'),
    netChangeInCash: reportedPeriodOrCurrencyChanged('financial.net_change_in_cash.current'),
    ev: reportedPeriodOrCurrencyChanged('financial.enterprise_value.current'),
    ebitMargin: calculationInputsChanged('financial.ebit_margin.current'),
    ebitdaMargin: calculationInputsChanged('financial.ebitda_margin.current'),
    preTaxProfitMargin: calculationInputsChanged('financial.pre_tax_profit_margin.current'),
    netProfitMargin: calculationInputsChanged('financial.net_profit_margin.current'),
    equityRatio: calculationInputsChanged('financial.equity_ratio.current'),
    netDebtToEbit: calculationInputsChanged('financial.net_debt_to_ebit.current'),
    netDebtToEbitda: calculationInputsChanged('financial.net_debt_to_ebitda.current'),
    netDebtToEquity: calculationInputsChanged('financial.net_debt_to_equity.current'),
    evToEbit: calculationInputsChanged('financial.enterprise_value_to_ebit.current'),
    evToEbitda: calculationInputsChanged('financial.enterprise_value_to_ebitda.current'),
    evToNetSales: calculationInputsChanged('financial.enterprise_value_to_revenue.current'),
    netSalesGrowth: calculationInputsChanged('financial.revenue.growth_1y'),
};
const sourceEvidenceFor = (v1Metric) => {
    const metric = (0, metric_catalog_1.findRealtimeMetric)(v1Metric);
    if (metric?.evidencePolicy === undefined) {
        throw new Error(`Legacy metric crosswalk references ${v1Metric} without a V1 evidence policy.`);
    }
    return {
        sourceSystems: [...metric.evidencePolicy.sourceSystems],
        temporalSemantics: metric.evidencePolicy.temporalSemantics,
        requiresObservedAt: metric.evidencePolicy.requiresObservedAt,
        ...(metric.coverage === undefined ? {} : { coverage: metric.coverage }),
        ...(metric.currencyPolicy === undefined ? {} : { currencyPolicy: metric.currencyPolicy }),
        ...(metric.dataScope === undefined ? {} : { dataScope: metric.dataScope }),
        ...(metric.workforceFactPolicy === undefined ? {} : { workforceFactPolicy: metric.workforceFactPolicy }),
    };
};
const crosswalkItemFor = (field) => {
    const base = {
        legacyKey: field.key,
        databaseKey: field.databaseKey,
        label: field.title,
        section: field.section,
        valueType: field.type,
    };
    const mapping = implementedLegacyMetricMappings[field.key];
    if (mapping === undefined) {
        return {
            ...base,
            disposition: 'planned',
            configurationTranslation: manualConfigurationTranslation,
        };
    }
    const sourceEvidence = sourceEvidenceFor(mapping.v1Metric);
    return mapping.disposition === 'mapped'
        ? {
            ...base,
            disposition: 'mapped',
            v1Metric: mapping.v1Metric,
            sourceEvidence,
            configurationTranslation: mapping.configurationTranslation,
        }
        : {
            ...base,
            disposition: 'semantic_change',
            v1Metric: mapping.v1Metric,
            sourceEvidence,
            semanticChange: mapping.semanticChange,
            configurationTranslation: mapping.configurationTranslation,
        };
};
/**
 * Complete machine-readable fact-level crosswalk for the canonical master-data
 * field catalog. A field mapping does not by itself promise that legacy filter
 * operators can be translated; `configurationTranslation` records that
 * separate migration-assessment capability.
 */
exports.legacyMetricMigrationCrosswalk = effect_1.effectDataFields.map(crosswalkItemFor);
/*
 * Everything from here to the end of the file exists only to serve
 * legacyMetricMigrationInventory, which is itself deprecated. no-deprecated has no
 * exemption for deprecated code that references deprecated code, so scope the rule off
 * to exactly this region rather than to the whole file -- a live reference added above
 * is still reported.
 */
/* eslint-disable @typescript-eslint/no-deprecated */
const sourcePolicyForSection = (section) => {
    switch (section) {
        case 'Company columns':
        case 'general':
        case 'handles':
            return { sourceFamily: 'company_master', provenanceReadiness: 'ready_for_raw_metric' };
        case 'financial':
            return { sourceFamily: 'financial_reporting', provenanceReadiness: 'requires_source_mapping' };
        case 'domain':
            return { sourceFamily: 'domain_crawl', provenanceReadiness: 'requires_source_mapping' };
        case 'buzz':
            return { sourceFamily: 'buzz', provenanceReadiness: 'ready_for_raw_metric' };
        case 'Jobposts':
            return { sourceFamily: 'jobtech', provenanceReadiness: 'ready_for_raw_metric' };
        default:
            return { sourceFamily: 'derived', provenanceReadiness: 'requires_input_evidence' };
    }
};
/** @deprecated Prefer the canonical `legacyMetricMigrationCrosswalk`. */
const legacyMetricMigrationInventory = () => master_data_1.masterDataInformationFields.map((field) => ({
    legacyKey: field.key,
    label: field.translation,
    section: field.section,
    valueType: field.type,
    ...sourcePolicyForSection(field.section),
}));
exports.legacyMetricMigrationInventory = legacyMetricMigrationInventory;
/* eslint-enable @typescript-eslint/no-deprecated */
//# sourceMappingURL=legacy-metric-inventory.js.map